import { FunctionComponent, useEffect, useState } from 'react';
import {
  FieldError,
  FieldErrors,
  Resolver,
  ResolverOptions,
  useForm,
} from 'react-hook-form';
import {
  FormFieldInput,
  LinkButtonV2,
  whitelistedStatusCodes,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import { useNavigate } from 'react-router-dom';

import { InputLabelTitle } from './InputLabelTitle';
import { VerifyEmailForm } from './types';
import { ActionBar } from './ActionBar';
import { useSignUp } from '../../common/hooks/useSignUp';
import { resendOtp, verifyOtp } from '../../common/api/sign-up';
import { SignUpStep } from '../../common/provider/SignUpContext';
import { stepRouteMapping } from '../signUpUtility';
import { ApiErrorResponse } from '../../common/api/types';
import { SIGN_UP_EMAIL_VERIFIED } from '../../common/constants';

const DEFAULT_CODE_LENGTH = 6;
export const verifyEmailFormResolver: Resolver<VerifyEmailForm> = (values) => {
  const errors = {} as FieldErrors<VerifyEmailForm>;
  if (!values.otpCode) {
    errors.otpCode = {
      message: 'Code is required',
    } as FieldError;
  } else if (
    values.otpCode.length < DEFAULT_CODE_LENGTH ||
    !new RegExp(/^\d+$/).test(values.otpCode)
  ) {
    errors.otpCode = {
      message: 'Invalid Code',
    } as FieldError;
  }

  return { values, errors };
};

export const VerifyEmailComponent: FunctionComponent = () => {
  const navigate = useNavigate();
  const { setSignUpStep, signUpCustomerEmail, setErrorMessage } = useSignUp();

  const isEmailVerified =
    JSON.parse(sessionStorage.getItem(SIGN_UP_EMAIL_VERIFIED) as string) ||
    false;

  const [isResendDisabled, setIsResendDisabled] = useState(true);

  useEffect(() => {
    let resendTimer: any = null;
    if (isResendDisabled) {
      resendTimer = setTimeout(() => {
        setIsResendDisabled(false);
      }, 30 * 1000);
    }
    return () => {
      clearTimeout(resendTimer);
    };
  }, [isResendDisabled]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: { otpCode: '' },
    resolver: (values) =>
      verifyEmailFormResolver(
        values,
        {},
        {} as ResolverOptions<VerifyEmailForm>
      ),
  });
  const onEmailVerified = () => {
    setSignUpStep(SignUpStep.selectPlan);
    navigate(stepRouteMapping[SignUpStep[SignUpStep.selectPlan]]);
  };

  const onSubmit = handleSubmit(async ({ otpCode }: VerifyEmailForm) => {
    try {
      const response = await verifyOtp(signUpCustomerEmail as string, otpCode);
      if (response.verified) {
        sessionStorage.setItem(
          SIGN_UP_EMAIL_VERIFIED,
          JSON.stringify(response.verified)
        );
        onEmailVerified();
      } else {
        setError('otpCode', {
          message: 'Incorrect code entered, please retry',
        });
      }
    } catch (e: any) {
      if (whitelistedStatusCodes.includes(e?.request?.status)) {
        const errorResponse: ApiErrorResponse = JSON.parse(e.request.response);
        setErrorMessage(errorResponse.error.description);
      } else {
        setErrorMessage('Please go back to the form and try again');
      }
    }
  });

  const onResend = async () => {
    setIsResendDisabled(true);
    try {
      const response = await resendOtp(signUpCustomerEmail as string);
      if (response.found) {
        setErrorMessage('An account exists with this email');
      }
    } catch (e: any) {
      if (whitelistedStatusCodes.includes(e?.request?.status)) {
        const errorResponse: ApiErrorResponse = JSON.parse(e.request.response);
        setErrorMessage(errorResponse.error.description);
      } else {
        setErrorMessage('Please go back to the form and try again');
      }
    }
  };

  return (
    <div className="w-full xl:w-5/6 px-6 xl:px-0 mb-24 overflow-auto">
      <div
        className={`flex flex-col w-full ${isEmailVerified ? '' : 'lg:w-[400px]'} pb-6 font-opensans`}
      >
        <div className="w-full flex flex-col mb-6">
          <span className="text-3xl font-thin text-primary-800">
            {isEmailVerified ? 'Account Verified' : 'Verify Your Account'}
          </span>
          <div className="text-sm text-slate-700 mt-2 leading-6">
            {isEmailVerified ? (
              <>
                This account has been verified with{' '}
                <span className="font-medium">{signUpCustomerEmail}</span>.
                <p>Go back if you would like to change it.</p>
              </>
            ) : (
              <>
                Please enter the one-time code we sent to your email address at{' '}
                <span className="font-medium">{signUpCustomerEmail}</span> to
                complete account setup.
              </>
            )}
          </div>
        </div>
        {!isEmailVerified && (
          <>
            <form className="w-full mb-8" onSubmit={onSubmit}>
              <FormFieldInput
                errors={errors}
                inputKey="otpCode"
                register={register}
                registerProps={{
                  required: true,
                }}
                testId="otp-code"
                className="w-full"
                inputClassName="shadow"
                maxLength={DEFAULT_CODE_LENGTH}
                title={
                  <InputLabelTitle
                    title="Enter Code"
                    hasError={Boolean(errors?.otpCode)}
                  />
                }
              />
            </form>
            <div className="flex flex-col w-full">
              <span className="text-sm text-slate-700 leading-6">
                OTPs may be requested once every 30 seconds and expire after 10
                minutes. Not seeing your code in your email?
              </span>
              <LinkButtonV2
                disabled={isResendDisabled}
                linkText="Request a new code"
                className="underline text-left leading-6 w-fit font-medium"
                onClick={onResend}
              />
            </div>
          </>
        )}
      </div>

      <ActionBar
        onSubmit={isEmailVerified ? onEmailVerified : onSubmit}
        isValid={isEmailVerified || isValid}
      />
    </div>
  );
};
